import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { useExperimentalFlag } from './useExperimentalFlag'
import { useGetPlaywrightAnalysesQuery } from '../services/projectDataApi'
import { permanentHttpDenial } from '../lib/permanentHttpDenial'

export function usePlaywrightTestsEnabled(projectId: string) {
  const user = useSelector((s: RootState) => s.auth.user)
  const { enabled } = useExperimentalFlag('playwright_tests')
  // Failure analyses live on the token/session-gated v1 API, and the browser holds no Authorization
  // credential — a signed-out visitor has no session cookie either, so every poll it sends is a
  // doomed 401. Skip while signed out, the same rule useExperimentalFlag applies to the settings
  // request.
  //
  // Issue #4032's latch, for the poll this hook shares that endpoint with on every project tab: a
  // deterministic 401/403 (a project this session cannot read) can never recover by retrying, and
  // the hook keeps rendering nothing from its result, so the poll just accumulated doomed requests
  // for as long as the page stayed open — 9 in the recorded session behind issue #5062, none
  // surfaced. Latch the denial per project and drop the interval to 0; transient failures (network
  // errors, 5xx) keep polling.
  const [deniedProjectId, setDeniedProjectId] = useState<string | null>(null)
  const analyses = useGetPlaywrightAnalysesQuery({ projectId, limit: 1 }, {
    skip: !projectId || !user,
    pollingInterval: deniedProjectId === projectId ? 0 : 15_000,
    skipPollingIfUnfocused: true,
  })
  useEffect(() => {
    if (permanentHttpDenial(analyses.error)) setDeniedProjectId(projectId)
  }, [analyses.error, projectId])
  return enabled || (analyses.currentData?.total ?? 0) > 0
}
